import { getCurrentToken } from '../utils/auth';
import { defaultConfig } from '../utils/config';
import { StreamContextType } from '../pages/game/components/stream/helpers/Types';
import { getToolDef, isHiddenToolName, isKnownToolName } from '../pages/game/components/stream/helpers/Tools';
import { startStream } from '../pages/game/components/stream/helpers/StreamOrchestrator';
import { sseTransportWithBody } from '../pages/game/components/stream/helpers/transports';
import { getLatestStreamContext } from '../pages/game/components/stream/helpers/StreamContext';
import { getStreamUIState, setActiveSession, setAwaitingFirstChunk, markStreamEventHeartbeat, setPendingTextBoundary } from '../pages/game/components/stream/helpers/streamStore';
import { streamLog } from '../pages/game/components/stream/helpers/streamLogger';

type GameArtRequest = {
  gameId: string;
  gameArtIds?: string[];
  gameHistoryId?: string;
  userPrompt?: string;
  operation: 'initialize_art_assets' | 'add_art_to_game' | 'create_or_update_assets_during_edit';
  streamContext?: StreamContextType;
}

interface generateGameArtProps {
  req: GameArtRequest;
}

type GameArtResult = {
  result: string;
  time: number;
};

// Helper function to handle SSE streaming response from agent mode
// Reuses the same event handling logic as game streaming
async function handleArtSSEStream(
  response: Response,
  streamContext: StreamContextType | undefined,
  startTime: number
): Promise<GameArtResult> {
  if (!streamContext) {
    // No stream context, just consume the stream
    const reader = response.body!.getReader();
    while (true) {
      const { done } = await reader.read();
      if (done) break;
    }
    return { result: 'Art integration completed', time: Date.now() - startTime };
  }

  streamContext.clearContent();
  streamContext.setIsStreaming(true);

  let accumulatedContent = '';
  const pendingIdsByName = new Map<string, string[]>();
  const countersByName = new Map<string, number>();

  const assignIdForName = (name: string) => {
    const n = (countersByName.get(name) || 0) + 1;
    countersByName.set(name, n);
    const id = `tool_${name}_${n}`;
    const q = pendingIdsByName.get(name) || [];
    q.push(id);
    pendingIdsByName.set(name, q);
    return id;
  };

  const takePendingIdForName = (name: string) => {
    const q = pendingIdsByName.get(name) || [];
    const id = q.shift();
    pendingIdsByName.set(name, q);
    return id;
  };

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('No readable body for stream');
  }

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Process complete SSE events separated by double newlines (matching StreamManager.connectViaFetch)
      const events = buffer.split(/\r?\n\r?\n/);
      buffer = events.pop() || '';

      for (const rawEvent of events) {
        const lines = rawEvent.split(/\r?\n/);
        let eventType = '';
        const dataLines: string[] = [];

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7).trim();
          } else if (line.startsWith('data: ') || line.startsWith('data:')) {
            const idx = line.indexOf('data:') + 5;
            const after = line.slice(idx);
            const payload = after.startsWith(' ') ? after.slice(1) : after;
            dataLines.push(payload);
          }
        }

        if (dataLines.length === 0) continue;

        const dataStr = dataLines.join('\n');

        try {
          const data = dataStr ? JSON.parse(dataStr) : {};

          // Use same event handling as StreamManager.handleEvent()
          if (eventType === 'agent_metadata') {
            if (data?.type === 'phase_transition' && data?.to) {
              streamContext.setStreamPhase?.(data.to);
            }
            if (data?.phase) {
              streamContext.setStreamPhase?.(data.phase);
            }
            // Boundary metadata can arrive here; handled by higher-level CodeAI via transports
          } else if (eventType === 'agent_tool_call') {
            const payload = (data && typeof data === 'object' && 'data' in (data as any)) ? (data as any).data : data;
            const name = (payload as any).toolName || (payload as any).type || 'Tool';
            if (isHiddenToolName(name as string)) {
              continue; // Always suppress hidden tools
            }
            if (!defaultConfig.DEBUG_STREAM_TOOLS && !isKnownToolName(name as string)) {
              continue; // Suppress unknown tools in non-debug
            }
            const id = (payload as any).toolId || (payload as any).id || assignIdForName(name);
            const args = (payload && typeof payload === 'object') ? ((payload as any).args || {}) : {};
            const meta = (payload && typeof payload === 'object') ? ((payload as any).meta || {}) : {};
            const params = { args, meta };
            const def = getToolDef(name);
            const vm = def.getInitial({ id, name, params, startTime: Date.now() });
            streamContext.addTool(vm as any);
          } else if (eventType === 'agent_tool_result') {
            const payload = (data && typeof data === 'object' && 'data' in (data as any)) ? (data as any).data : data;
            const name = (payload as any).toolName || (payload as any).type || (payload as any).name || 'Tool';
            if (isHiddenToolName(name as string)) {
              continue; // Always suppress hidden tools
            }
            if (!defaultConfig.DEBUG_STREAM_TOOLS && !isKnownToolName(name as string)) {
              continue; // Suppress unknown tools in non-debug
            }
            const id = (payload as any).toolId || (payload as any).id || takePendingIdForName(name) || `tool_${name}_result`;
            const result = (payload && typeof payload === 'object') ? ((payload as any).result || {}) : {};
            const meta = (payload && typeof payload === 'object') ? ((payload as any).meta || {}) : {};
            const merged = { ...result, meta };
            const def = getToolDef(name);
            streamContext.updateTool(id, (existing: any) => def.updateWithResult(existing, merged));
          } else if (eventType === 'agent_ai_content') {
            const payload = (data && typeof data === 'object' && 'data' in (data as any)) ? (data as any).data : data;
            const chunk: string = typeof (payload as any)?.content === 'string'
              ? (payload as any).content
              : (typeof (payload as any)?.deltaContent === 'string' ? (payload as any).deltaContent : '');
            if (chunk) {
              const meta = (payload && typeof payload === 'object') ? ((payload as any).meta || {}) : {};
              streamContext.addTextContent(chunk, meta);
              accumulatedContent += chunk;
            }
          } else if (eventType === 'agent_complete') {
            streamContext.setIsStreaming(false);
          } else if (eventType === 'agent_done') {
            streamContext.setIsStreaming(false);
            break;
          } else if (eventType === 'agent_error') {
            streamContext.setIsStreaming(false);
            const message = data.error || 'Stream error';
            throw new Error(message);
          }
        } catch (parseError) {
          console.warn('[Art Stream] Failed to parse event data:', parseError);
        }
      }
    }
  } catch (error) {
    streamContext.setIsStreaming(false);
    throw error;
  } finally {
    reader.releaseLock();
    streamContext.setIsStreaming(false);
  }

  return {
    result: accumulatedContent || 'Art integration completed',
    time: Date.now() - startTime
  };
}

export const generateGameArt = async ({ req }: generateGameArtProps): Promise<GameArtResult> => {
  const startTime = Date.now();
  const functionURL = process.env.REACT_APP_SUPABASE_FUNCTIONS_URL;

  const useCloudflare = defaultConfig.USE_CLOUDFLARE_GAME_STREAM;
  const cfStreamURL = process.env.REACT_APP_CLOUDFLARE_GAME_STREAM_URL;
  const awsStreamURL = process.env.REACT_APP_STREAM_LAMBDA_URL;

  const streamURL = (useCloudflare && cfStreamURL) ? cfStreamURL : awsStreamURL;

  const authToken = await getCurrentToken();

  if (!streamURL) {
    console.error('No stream URL provided: ', streamURL);
  }

  // If adding art during edit and new stream URL is enabled, route to the game-stream lambda via the same SSE machinery as game
  if (req.operation === 'add_art_to_game' && defaultConfig.GAME_ART_NEW_STREAM_URL && streamURL) {
    const lambdaBody = {
      type: 'ART',
      gameId: req.gameId,
      gameArtIds: req.gameArtIds,
      gameHistoryId: req.gameHistoryId,
      userPrompt: req.userPrompt,
    };

    const sessionId = `art_${req.gameId}_${Date.now()}`;
    const streamCtx = req.streamContext || getLatestStreamContext() || null;
    const body = lambdaBody;

    if (streamCtx) {
      try {
        streamCtx.clearContent();
        streamCtx.setIsStreaming(true);
        streamCtx.setStreamPhase?.('Connecting');
      } catch (_) { }
    }

    // Announce session and orchestrate events like CodeAI
    try { setActiveSession(sessionId); } catch (_) { }
    try { setAwaitingFirstChunk(true); } catch (_) { }

    let accumulated = '';
    const run = startStream({
      primary: sseTransportWithBody(streamURL.replace(/\/$/, ''), {
        type: 'ART',
        gameId: body.gameId,
        gameArtIds: body.gameArtIds,
        gameHistoryId: body.gameHistoryId,
        userPrompt: body.userPrompt,
      }),
      args: {
        request: { prompt: body.userPrompt || '', historyEntryId: body.gameHistoryId || null, gameId: String(body.gameId), isInitialCreate: false },
        headers: { 'Authorization': `Bearer ${authToken}` },
        startTimeMs: startTime,
      },
      onEvent: (e) => {
        try { markStreamEventHeartbeat(); } catch (_) { }
        const live = req.streamContext || getLatestStreamContext();
        if (!live) return;
        if (e.type === 'phase') {
          live.setStreamPhase?.(e.to);
        } else if (e.type === 'text_delta') {
          try {
            const eventMeta = (e as any)?.meta || {};
            const eventPhase = (e as any)?.phase;
            if (eventPhase === 'sub_agent') {
              const missing: string[] = [];
              if (typeof (eventMeta as any)?.task_tool_id !== 'string') missing.push('task_tool_id');
              if (typeof (eventMeta as any)?.sub_agent_role !== 'string') missing.push('sub_agent_role');
              if (missing.length > 0) streamLog.warn('[sub_agent][missing_tags] text_delta', { missing, meta: eventMeta });
            }
          } catch (_) { }
          try { setAwaitingFirstChunk(false); } catch (_) { }
          accumulated += e.delta;
          live.addTextContent(e.delta, (e as any).meta);
        } else if (e.type === 'tool_call') {
          const def = getToolDef(e.name || 'Tool');
          const eventMeta = (e as any)?.meta || {};
          try {
            const eventPhase = (e as any)?.phase;
            if (eventPhase === 'sub_agent') {
              const missing: string[] = [];
              if (typeof (eventMeta as any)?.task_tool_id !== 'string') missing.push('task_tool_id');
              if (typeof (eventMeta as any)?.sub_agent_role !== 'string') missing.push('sub_agent_role');
              if (missing.length > 0) streamLog.warn('[sub_agent][missing_tags] tool_call', { missing, meta: eventMeta, id: (e as any)?.id, name: e.name });
            }
          } catch (_) { }
          const baseParams = (e as any)?.params || {};
          const mergedParams = (typeof (eventMeta as any)?.task_tool_id === 'string')
            ? ({ ...baseParams, meta: { ...((baseParams as any)?.meta || {}), ...eventMeta } } as any)
            : (baseParams as any);
          const vm = def.getInitial({ id: e.id, name: e.name || 'Tool', params: mergedParams, startTime: Date.now() });
          live.addTool(vm as any);
        } else if (e.type === 'tool_result') {
          const def = getToolDef(e.name || 'Tool');
          const eventMeta = (e as any)?.meta || {};
          try {
            const eventPhase = (e as any)?.phase;
            if (eventPhase === 'sub_agent') {
              const missing: string[] = [];
              if (typeof (eventMeta as any)?.task_tool_id !== 'string') missing.push('task_tool_id');
              if (typeof (eventMeta as any)?.sub_agent_role !== 'string') missing.push('sub_agent_role');
              if (missing.length > 0) streamLog.warn('[sub_agent][missing_tags] tool_result', { missing, meta: eventMeta, id: (e as any)?.id, name: e.name });
            }
          } catch (_) { }
          const resultBase = (e as any)?.result || {};
          const resultWithMeta = (typeof (eventMeta as any)?.task_tool_id === 'string')
            ? ({ ...resultBase, meta: { ...((resultBase as any)?.meta || {}), ...eventMeta } } as any)
            : (resultBase as any);
          live.updateTool(e.id, (existing: any) => def.updateWithResult(existing, resultWithMeta as any));
        } else if (e.type === 'complete') {
          live.setIsStreaming(false);
        } else if (e.type === 'error') {
          live.setIsStreaming(false);
        } else if (e.type === 'metadata') {
          try {
            const payload: any = (e as any).payload;
            if (payload && payload.type === 'ai_response_boundary') {
              setPendingTextBoundary(true);
            }
          } catch (_) { }
        }
      }
    });

    const result = await run.done;
    try {
      const { activeSessionId } = getStreamUIState() as any;
      if (activeSessionId && sessionId && activeSessionId === sessionId) {
        setActiveSession(null);
      }
    } catch (_) { }
    return { result: result.raw, time: result.timeMs };
  }

  // Prepare the request body for the edge function fallback
  const requestBody = {
    gameId: req.gameId,
    gameArtIds: req.gameArtIds,
    gameHistoryId: req.gameHistoryId,
    userPrompt: req.userPrompt,
    operation: req.operation
  };

  if (!functionURL) {
    console.error('No Supabase URL provided: ', functionURL);
  }

  const response = await fetch(`${functionURL}/generate-game-art`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const error = await response.text();
    console.error('Error from Edge Function:', error);
    throw new Error(`Failed to generate game art: ${error}`);
  }

  if (!response.body) {
    throw new Error('No response body received');
  }

  let accumulatedResult = '';
  const streamContext = req.streamContext;

  if (streamContext) {
    streamContext.clearContent();
    streamContext.setIsStreaming(true);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value);
      accumulatedResult += chunk;

      // Update streaming content if context is available
      if (streamContext && chunk) {
        streamContext.addTextContent(chunk);
      }
    }
  } finally {
    if (streamContext) {
      streamContext.setIsStreaming(false);
    }
    reader.releaseLock();
  }

  const endTime = Date.now();
  const executionTime = endTime - startTime;

  return {
    result: accumulatedResult,
    time: executionTime,
  };
};

// Helper function to initialize art assets for a game
export const initializeGameArtAssets = async (gameId: string): Promise<GameArtResult> => {
  return generateGameArt({
    req: {
      gameId,
      operation: 'initialize_art_assets'
    }
  });
};

// Helper function to add art to a game
export const addArtToGame = async (
  gameId: string,
  gameArtIds: string[],
  gameHistoryId: string,
  userPrompt?: string,
  streamContext?: StreamContextType
): Promise<GameArtResult> => {
  return generateGameArt({
    req: {
      gameId,
      gameArtIds,
      gameHistoryId,
      userPrompt,
      operation: 'add_art_to_game',
      streamContext
    }
  });
};

// Helper function to create new assets during game editing
export const createOrUpdateAssetsDuringEdit = async (
  gameId: string,
  gameHistoryId: string,
  userPrompt: string
): Promise<GameArtResult> => {
  return generateGameArt({
    req: {
      gameId,
      gameHistoryId,
      userPrompt,
      operation: 'create_or_update_assets_during_edit'
    }
  });
};